// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES, DOSSIER_NOTARIS } from '@/lib/constants/routes'
import { PrimaryActionButton } from '@/components/ui/PrimaryActionButton'
import { SecondaryActionButton } from '@/components/ui/SecondaryActionButton'
import { useReturnNav } from '@/lib/returnNav'
import { NextStepBlock } from '@/components/process/NextStepBlock'
import { ClosingGuidance } from '@/components/process/ClosingGuidance'
import { Plus, Trash2, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react'
import { SaveStatusBadge } from '@/components/ui/SaveStatusBadge'
import { InfoButton }      from '@/components/ui/InfoButton'
import { ModuleShell }     from '@/components/ui/ModuleShell'
import { InfoBlock }       from '@/components/ui/InfoBlock'
import { Input }           from '@/components/ui/Input'
import { Select }          from '@/components/ui/Select'
import { Textarea }        from '@/components/ui/Textarea'
import { Button }          from '@/components/ui/Button'
import { Card }            from '@/components/ui/Card'
import { LoadingState }    from '@/components/feedback/LoadingState'
import { useAuthStore, useWizardStore } from '@/store'
import { getFirstProfile } from '@/services/profile'
import { loadPartner, loadChildren, type PartnerData, type ChildrenData } from '@/services/wizard'
import { applyKnownPerson, fromPartner, fromChild } from '@/lib/personReuse'
import { loadTrustedPersons, type TrustedPersonConfig } from '@/services/trustedPerson'
import { useAutosave }     from '@/hooks/useAutosave'
import {
  loadNotarial, saveNotarial, EMPTY_NOTARIAL,
  type NotarialData, type BezittingEntry, type DigitaalEntry, type GoedDoelEntry,
} from '@/services/notarial'


const GOEDE_DOELEN_OPTIES = [
  'KWF Kankerbestrijding',
  'Hartstichting',
  'Longfonds',
  'Rode Kruis',
  'CliniClowns',
  'Dierenbescherming',
  'Natuurmonumenten',
  'KiKa',
  'Alzheimer Nederland',
  'Voedselbanken Nederland',
]

const DIGITALE_ACTIES = [
  'Verwijderen','Laten bestaan','Overdragen','Archiveren','Herdenkingsprofiel instellen','Anders',
].map(v => ({ value: v, label: v }))

const BEKENDE_PLATFORMS = [
  'Facebook','Instagram','LinkedIn','Twitter/X','Google/Gmail','WhatsApp','Dropbox','iCloud',
  'YouTube','TikTok','Pinterest','Reddit','Spotify','Netflix','Anders',
].map(v => ({ value: v, label: v }))

// ── Collapsible section ───────────────────────────────────────────────────────
function Section({
  title, subtitle, infoTitle, infoContent, defaultOpen = true, children,
}: {
  title: string; subtitle?: string; infoTitle?: string; infoContent?: React.ReactNode
  defaultOpen?: boolean; children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-2xl border border-sand-300 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-sand-50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="flex-1">
            <p className="text-sm font-semibold text-ink">{title}</p>
            {subtitle && !open && <p className="text-xs text-ink-4 mt-0.5">{subtitle}</p>}
          </div>
          {infoTitle && infoContent && (
            <span onClick={e => e.stopPropagation()}>
              <InfoButton title={infoTitle} content={infoContent} />
            </span>
          )}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-ink-4 flex-shrink-0 ml-2" />
               : <ChevronDown className="h-4 w-4 text-ink-4 flex-shrink-0 ml-2" />}
      </button>
      {open && <div className="px-5 pb-5 flex flex-col gap-3">{children}</div>}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

const RELATIE_OPTIONS = [
  { value: 'Partner',         label: 'Partner' },
  { value: 'Kind',            label: 'Kind' },
  { value: 'Vertrouwenspersoon', label: 'Vertrouwenspersoon' },
  { value: 'Vader',           label: 'Vader' },
  { value: 'Moeder',          label: 'Moeder' },
  { value: 'Broer',           label: 'Broer' },
  { value: 'Zus',             label: 'Zus' },
  { value: 'Oom',             label: 'Oom' },
  { value: 'Tante',           label: 'Tante' },
  { value: 'Grootouder',      label: 'Grootouder' },
  { value: 'Vriend',          label: 'Vriend' },
  { value: 'Vriendin',        label: 'Vriendin' },
  { value: 'Buur',            label: 'Buur' },
  { value: 'Overige familie', label: 'Overige familie' },
  { value: 'Anders',          label: 'Anders' },
]

export function NotarialPage() {
  const { userId }  = useAuthStore()
  const navigate    = useNavigate()
  const { target, label } = useReturnNav()
  const { answers } = useWizardStore()
  const [data,      setData]      = useState<NotarialData>(EMPTY_NOTARIAL)
  const [isLoading, setIsLoading] = useState(true)
  const [status,    setStatus]    = useState<'NOT_STARTED'|'IN_PROGRESS'|'COMPLETE'>('NOT_STARTED')
  // V39: context voor suggesties
  const [ctxPartner,   setCtxPartner]   = useState<PartnerData | null>(null)
  const [ctxChildren,  setCtxChildren]  = useState<ChildrenData | null>(null)
  const [ctxVP,        setCtxVP]        = useState<TrustedPersonConfig[]>([])


  useEffect(() => {
    if (!userId) return
    Promise.all([
      loadNotarial(userId),
      getFirstProfile(), // eslint-disable-next-line @typescript-eslint/no-unused-vars
      loadPartner(userId),
      loadChildren(userId),
      loadTrustedPersons(userId),
    ]).then(([loaded, _profile, partner, children, vps]) => {
      void _profile
      if (loaded) setData(loaded)
      if (partner) setCtxPartner(partner)
      if (children) setCtxChildren(children)
      if (vps?.length) setCtxVP(vps)
      setIsLoading(false)
    })
  }, [userId])

  function recalc(d: NotarialData) {
    // V36: COMPLETE zodra minimaal één kernveld ingevuld is (executeur of erfgenaanwensen)
    // Amber "Bijna compleet" verwijderd - optionele velden bepalen niet meer de status
    const hasCore = !!(d.executeur?.name && d.erfgenaanWensen)
    const hasAny  = !!d.executeur?.name || !!d.erfgenaanWensen ||
                    d.bezittingen?.length > 0 || d.goedeDoelenLijst?.length > 0 ||
                    d.digitaleAccounts?.length > 0 || !!d.persoonlijkeBoodschap
    setStatus(hasCore ? 'COMPLETE' : hasAny ? 'IN_PROGRESS' : 'NOT_STARTED')
  }

  const save = useCallback(async (d: NotarialData) => {
    if (!userId) return
    await saveNotarial(userId, d)
  }, [userId])

  const { status: saveStatus, saveNow } = useAutosave({ data, onSave: save, enabled: !isLoading, delay: 700 })

  function upd<K extends keyof NotarialData>(key: K, value: NotarialData[K]) {
    setData(prev => { const n = { ...prev, [key]: value }; recalc(n); return n })
  }

  // ── List helpers ─────────────────────────────────────────────────────────────
  function addBezitting() {
    upd('bezittingen', [...data.bezittingen, { bezitting: '', persoon: '', opmerking: '' }])
  }
  function updBezitting(i: number, f: keyof BezittingEntry, v: string) {
    const arr = [...data.bezittingen]; arr[i] = { ...arr[i], [f]: v }; upd('bezittingen', arr)
  }
  function delBezitting(i: number) { upd('bezittingen', data.bezittingen.filter((_, j) => j !== i)) }

  function addDigitaal() {
    upd('digitaleAccounts', [...data.digitaleAccounts, { platform: '', actie: '' }])
  }
  function updDigitaal(i: number, f: keyof DigitaalEntry, v: string) {
    const arr = [...data.digitaleAccounts]; arr[i] = { ...arr[i], [f]: v }; upd('digitaleAccounts', arr)
  }
  function delDigitaal(i: number) { upd('digitaleAccounts', data.digitaleAccounts.filter((_, j) => j !== i)) }

  function addGoedDoel() {
    upd('goedeDoelenLijst', [...data.goedeDoelenLijst, { organisatie: '', bedragOfPct: '', opmerking: '' }])
  }
  function updGoedDoel(i: number, f: keyof GoedDoelEntry, v: string) {
    const arr = [...data.goedeDoelenLijst]; arr[i] = { ...arr[i], [f]: v }; upd('goedeDoelenLijst', arr)
  }
  function delGoedDoel(i: number) { upd('goedeDoelenLijst', data.goedeDoelenLijst.filter((_, j) => j !== i)) }

  function addBuitenland() {
    upd('buitenlandseBezittingen', [...data.buitenlandseBezittingen, { land: '', omschrijving: '', bijzonderheden: '' }])
  }

  if (isLoading) return <LoadingState message="Laden..." />

  // Dynamische nummering: reset per render, telt alleen zichtbare secties
  let _sectionNum = 0
  const sNum = () => { _sectionNum += 1; return _sectionNum }

  return (
    <div>
      <button onClick={async () => { await saveNow(); navigate(ROUTES.DASHBOARD) }}
        className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors mb-5 group">
        <svg className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
        Terug naar dashboard
      </button>
      <ModuleShell
        title="Notariële wensen & nalatenschap"
        description="Denk alvast na over belangrijke keuzes rond uw nalatenschap, nu u die keuzes nog zelf kunt maken. WilsApp bewaart deze wensen als overzicht en als voorbereiding op een gesprek met uw notaris."
        status={status}
        actions={<SaveStatusBadge status={saveStatus} />}
      >
        <InfoBlock title="Waarom dit nu vastleggen?" variant="neutral" defaultOpen={false}>
          <p>Officiële papieren liggen vaak verspreid: een akte hier, een polis daar, een notaris uit een andere stad. Hier brengt u dat bij elkaar, terwijl u uw keuzes nog rustig kunt overwegen, zodat een gesprek met de notaris later soepeler verloopt.</p>
          <p>WilsApp legt zelf niets juridisch vast; voor de officiële vastlegging blijft een notaris nodig.</p>
        </InfoBlock>

        {/* V36: Compleetheidsindicator - alleen groen bij kernveld gevuld, geen amber druk */}
        {status === 'COMPLETE' && (
          <div className="flex items-center gap-2.5 rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
            <span className="text-base flex-shrink-0">✅</span>
            <p className="text-sm font-medium text-sage-800">
              Uw wensen zijn opgeslagen. U kunt dit aanvullen vóór een gesprek met uw notaris.
            </p>
          </div>
        )}
        {/* Intro: wat leg je hier vast + waarom */}
        <div className="rounded-xl bg-sand border border-sand-200 px-4 py-3.5">
          <p className="text-sm text-ink-2 leading-relaxed">
            Op deze pagina denkt u na over: <strong>executeur</strong>, <strong>voogdij</strong>, <strong>erfgenamen</strong>,
            <strong> legaten</strong>, <strong>digitale nalatenschap</strong>, bijzondere bezittingen en persoonlijke wensen.
          </p>
          <p className="text-sm text-ink-3 leading-relaxed mt-2">
            Door dit vooraf te doordenken maakt u een gesprek met de notaris eenvoudiger en weten uw nabestaanden wat u wenste.
          </p>
        </div>

        {/* Juridisch blok - onderscheid wens / voorbereiding / bindend */}
        <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3.5">
          <p className="text-xs font-semibold text-blue-900 uppercase tracking-wider mb-2">Goed om te weten</p>
          <ul className="space-y-1.5 text-sm text-blue-900/90 leading-relaxed">
            <li>📝 <strong>Wens:</strong> WilsApp bewaart uw wensen en helpt u overzicht te maken.</li>
            <li>🗂️ <strong>Voorbereiding:</strong> gebruik deze pagina als voorbereiding op een gesprek met een notaris.</li>
            <li>⚖️ <strong>Juridisch bindend:</strong> alleen een notarieel testament of andere officiële akte is juridisch bindend. WilsApp vervangt dit niet.</li>
          </ul>
        </div>

        {/* 1. Executeur */}
        <Section title={`${sNum()}. Executeur`} subtitle="Wie regelt na uw overlijden de praktische en financiële afwikkeling?"
          infoTitle="Executeur"
          infoContent={<>
            <p>Een executeur regelt na uw overlijden praktische en financiële zaken van de nalatenschap.</p>
            <p><strong>Wat doet een executeur?</strong></p>
            <ul className="list-disc list-inside text-xs text-ink-3 space-y-0.5 mt-1">
              <li>Bankzaken afhandelen</li>
              <li>Belastingaangifte doen</li>
              <li>Contact onderhouden met erfgenamen</li>
              <li>Bezittingen afwikkelen en verdelen</li>
            </ul>
            <p className="mt-2"><strong>Niet verplicht</strong>, maar wel aan te raden. De executeur moet worden benoemd via een notarieel testament.</p>
            <p><strong>Voorbeeld:</strong> uw partner, een van uw kinderen of uw boekhouder.</p>
            <p className="mt-2">
              <a href="https://www.rijksoverheid.nl/onderwerpen/erven" target="_blank" rel="noopener noreferrer"
                 className="text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
                Rijksoverheid - Erfenis regelen →
              </a>
            </p>
            <p className="text-xs text-ink-4 mt-1 leading-relaxed">
              WilsApp legt uw wensen vast als persoonlijke leidraad. Voor juridisch bindende benoeming verwijzen wij naar uw notaris.
            </p>
          </>}
        >
          <p className="text-sm text-ink-3">Wie wilt u aanwijzen als executeur van uw nalatenschap?</p>

          {/* V39: Suggestieknoppen */}
          {(ctxPartner?.hasPartner === 'yes' || ctxChildren?.hasChildren || ctxVP.length > 0) && (
            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-semibold text-ink-4 uppercase tracking-wide">Suggesties uit uw dossier</p>
              <div className="flex flex-wrap gap-2">
                {ctxPartner?.hasPartner === 'yes' && ctxPartner.name && (
                  <button type="button"
                    onClick={() => upd('executeur', applyKnownPerson({ ...data.executeur, relation: 'Partner' }, fromPartner(ctxPartner), { fullName: 'name', phone: 'phone', dob: 'dob' }))}
                    className="flex items-center gap-1.5 rounded-xl border border-sage-300 bg-sage-50 px-3 py-1.5 text-xs font-medium text-sage-700 hover:bg-sage-100 transition-colors">
                    👤 Partner: {ctxPartner.name} {ctxPartner.lastName || ''}
                  </button>
                )}
                {ctxVP.map(vp => (
                  <button key={vp.id} type="button"
                    onClick={() => upd('executeur', { ...data.executeur, name: vp.firstName, relation: 'Vertrouwenspersoon', phone: data.executeur.phone })}
                    className="flex items-center gap-1.5 rounded-xl border border-sage-300 bg-sage-50 px-3 py-1.5 text-xs font-medium text-sage-700 hover:bg-sage-100 transition-colors">
                    🤝 VP: {vp.firstName}
                  </button>
                ))}
                {ctxChildren?.children?.filter(c => c.name).map((c, i) => (
                  <button key={i} type="button"
                    onClick={() => upd('executeur', applyKnownPerson({ ...data.executeur, relation: 'Kind' }, fromChild(c), { fullName: 'name', dob: 'dob' }))}
                    className="flex items-center gap-1.5 rounded-xl border border-sage-300 bg-sage-50 px-3 py-1.5 text-xs font-medium text-sage-700 hover:bg-sage-100 transition-colors">
                    👶 Kind: {c.name} {c.lastName || ''}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Naam executeur" value={data.executeur.name}
              onChange={e => upd('executeur', { ...data.executeur, name: e.target.value })}
              placeholder="Voor- en achternaam" />
            <Select label="Relatie" value={data.executeur.relation || ''}
              options={RELATIE_OPTIONS} placeholder="Kies relatie"
              onChange={e => upd('executeur', { ...data.executeur, relation: e.target.value })} />
          </div>
          <Input label="Telefoonnummer" type="tel" value={data.executeur.phone}
            onChange={e => upd('executeur', { ...data.executeur, phone: e.target.value })}
            placeholder="06-..." />
          <Input label="Geboortedatum (optioneel)" type="date" value={data.executeur.dob ?? ''}
            onChange={e => upd('executeur', { ...data.executeur, dob: e.target.value })} />

          <div className="border-t border-sand-200 pt-3">
            <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Tweede executeur (optioneel)</p>
            {/* V39: Suggesties reserve */}
            {(ctxPartner?.hasPartner === 'yes' || ctxChildren?.hasChildren || ctxVP.length > 0) && (
              <div className="flex flex-wrap gap-2 mb-3">
                {ctxPartner?.hasPartner === 'yes' && ctxPartner.name && (
                  <button type="button"
                    onClick={() => upd('executeur', applyKnownPerson({ ...data.executeur, reserveRelation: 'Partner' }, fromPartner(ctxPartner), { fullName: 'reserveName', phone: 'reservePhone', dob: 'reserveDob' }))}
                    className="flex items-center gap-1.5 rounded-xl border border-sand-300 bg-sand px-3 py-1.5 text-xs text-ink-3 hover:bg-sand-200 transition-colors">
                    👤 {ctxPartner.name}
                  </button>
                )}
                {ctxVP.map(vp => (
                  <button key={vp.id} type="button"
                    onClick={() => upd('executeur', { ...data.executeur, reserveName: vp.firstName, reserveRelation: 'Vertrouwenspersoon', reservePhone: data.executeur.reservePhone })}
                    className="flex items-center gap-1.5 rounded-xl border border-sand-300 bg-sand px-3 py-1.5 text-xs text-ink-3 hover:bg-sand-200 transition-colors">
                    🤝 {vp.firstName}
                  </button>
                ))}
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Naam" value={data.executeur.reserveName}
                onChange={e => upd('executeur', { ...data.executeur, reserveName: e.target.value })}
                placeholder="Voor- en achternaam" />
              <Select label="Relatie" value={data.executeur.reserveRelation || ''}
                options={RELATIE_OPTIONS} placeholder="Kies relatie"
                onChange={e => upd('executeur', { ...data.executeur, reserveRelation: e.target.value })} />
            </div>
            <Input label="Telefoonnummer" type="tel" value={data.executeur.reservePhone}
              onChange={e => upd('executeur', { ...data.executeur, reservePhone: e.target.value })}
              placeholder="06-..." className="mt-3" />
            <Input label="Geboortedatum (optioneel)" type="date" value={data.executeur.reserveDob ?? ''}
              onChange={e => upd('executeur', { ...data.executeur, reserveDob: e.target.value })} className="mt-3" />
          </div>
        </Section>

        {/* 2. Voogdij - alleen bij minderjarige kinderen */}
        {answers.hasChildren && (
          <Section title={`${sNum()}. Voogdij minderjarige kinderen`} subtitle="Wie zorgt voor uw minderjarige kinderen als u er niet meer bent?"
            infoTitle="Voogdij"
            infoContent={<>
              <p>Als beide ouders overlijden en er zijn nog minderjarige kinderen, dan bepaalt de rechter wie de voogd wordt - tenzij u dit zelf heeft vastgelegd.</p>
              <p><strong>Wettelijk vastleggen</strong> kan alleen via een notarieel testament of via een aantekening bij de rechtbank.</p>
              <p>Noteer hier uw wens als leidraad. Bespreek dit daarna met een notaris.</p>
            </>}
          >
            <p className="text-sm text-ink-3">Wie wilt u aanwijzen als voogd indien beide ouders overlijden?</p>
            {(ctxPartner?.hasPartner === 'yes' || ctxVP.length > 0) && (
              <div className="flex flex-wrap gap-2">
                <p className="w-full text-xs font-semibold text-ink-4 uppercase tracking-wide">Suggesties uit uw dossier</p>
                {ctxPartner?.hasPartner === 'yes' && ctxPartner.name && (
                  <button type="button"
                    onClick={() => upd('voogdij', applyKnownPerson({ ...data.voogdij, relation: 'Partner' }, fromPartner(ctxPartner), { fullName: 'name', phone: 'phone', dob: 'dob' }))}
                    className="flex items-center gap-1.5 rounded-xl border border-sage-300 bg-sage-50 px-3 py-1.5 text-xs font-medium text-sage-700 hover:bg-sage-100 transition-colors">
                    👤 Partner: {ctxPartner.name}
                  </button>
                )}
                {ctxVP.map(vp => (
                  <button key={vp.id} type="button"
                    onClick={() => upd('voogdij', { ...data.voogdij, name: vp.firstName, relation: 'Vertrouwenspersoon', phone: data.voogdij.phone })}
                    className="flex items-center gap-1.5 rounded-xl border border-sage-300 bg-sage-50 px-3 py-1.5 text-xs font-medium text-sage-700 hover:bg-sage-100 transition-colors">
                    🤝 VP: {vp.firstName}
                  </button>
                ))}
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Naam voogd" value={data.voogdij.name}
                onChange={e => upd('voogdij', { ...data.voogdij, name: e.target.value })}
                placeholder="Voor- en achternaam" />
              <Select label="Relatie" value={data.voogdij.relation || ''}
                options={RELATIE_OPTIONS} placeholder="Kies relatie"
                onChange={e => upd('voogdij', { ...data.voogdij, relation: e.target.value, relationOther: e.target.value !== 'Anders' ? '' : data.voogdij.relationOther })} />
            </div>
            {data.voogdij.relation === 'Anders' && (
              <Input label="Relatie toelichten" value={data.voogdij.relationOther}
                onChange={e => upd('voogdij', { ...data.voogdij, relationOther: e.target.value })}
                placeholder="Bijv: Peetouder, Mentor" />
            )}
            <Input label="Telefoonnummer" type="tel" value={data.voogdij.phone}
              onChange={e => upd('voogdij', { ...data.voogdij, phone: e.target.value })}
              placeholder="06-..." />
            <Input label="Geboortedatum (optioneel)" type="date" value={data.voogdij.dob ?? ''}
              onChange={e => upd('voogdij', { ...data.voogdij, dob: e.target.value })} />
            <Textarea label="Aanvullende notities" value={data.voogdij.notes}
              onChange={e => upd('voogdij', { ...data.voogdij, notes: e.target.value })}
              placeholder="Bijv: Mijn zus is bereid dit te doen, wij hebben dit samen besproken..." maxLength={300} />

            {/* Tweede voogd (V42) */}
            <div className="mt-2">
              <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-3">Tweede voogd (optioneel)</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Naam" value={data.voogdij.reserveName ?? ''}
                  onChange={e => upd('voogdij', { ...data.voogdij, reserveName: e.target.value })}
                  placeholder="Voor- en achternaam" />
                <Select label="Relatie" value={data.voogdij.reserveRelation ?? ''}
                  options={RELATIE_OPTIONS} placeholder="Kies relatie"
                  onChange={e => upd('voogdij', { ...data.voogdij, reserveRelation: e.target.value, reserveRelationOther: e.target.value !== 'Anders' ? '' : (data.voogdij.reserveRelationOther ?? '') })} />
              </div>
              {(data.voogdij.reserveRelation ?? '') === 'Anders' && (
                <Input label="Relatie toelichten" value={data.voogdij.reserveRelationOther ?? ''}
                  onChange={e => upd('voogdij', { ...data.voogdij, reserveRelationOther: e.target.value })}
                  placeholder="Bijv: Peetouder, Mentor" className="mt-3" />
              )}
              <Input label="Telefoonnummer" type="tel" value={data.voogdij.reservePhone ?? ''}
                onChange={e => upd('voogdij', { ...data.voogdij, reservePhone: e.target.value })}
                placeholder="06-..." className="mt-3" />
              <Input label="Geboortedatum (optioneel)" type="date" value={data.voogdij.reserveDob ?? ''}
                onChange={e => upd('voogdij', { ...data.voogdij, reserveDob: e.target.value })} className="mt-3" />
            </div>

            <div className="rounded-xl bg-status-warnBg border border-status-warnBorder px-4 py-3 text-xs text-status-warnText">
              Deze wens moet uiteindelijk via een notaris of rechtbank worden vastgelegd om rechtsgeldig te zijn.
            </div>
          </Section>
        )}

        {/* 3. Erfgenamen */}
        <Section title={`${sNum()}. Erfgenamen - bijzondere wensen`} subtitle="Wie erft wat? Leg afwijkingen van het wettelijk erfrecht vast."
          infoTitle="Erfgenamen"
          infoContent={<>
            <p>Erfgenamen ontvangen (een deel van) uw nalatenschap. Als er geen testament is, bepaalt de wet wie uw erfgenamen zijn.</p>
            <p className="mt-2"><strong>Wettelijk erfrecht zonder testament:</strong> uw partner en kinderen erven automatisch in gelijke delen.</p>
            <p className="mt-2"><strong>Wilt u afwijken?</strong> Dat kan via een testament bij de notaris. Noteer hier uw wensen als leidraad.</p>
            <p><strong>Voorbeelden:</strong> kinderen gelijk behandelen, stiefkinderen opnemen, bepaalde personen uitsluiten, een bedrag voor een kleinkind.</p>
            <p className="mt-2">
              <a href="https://www.rijksoverheid.nl/onderwerpen/erven" target="_blank" rel="noopener noreferrer"
                 className="text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
                Rijksoverheid - Erfgenamen en erfenis →
              </a>
            </p>
            <p className="text-xs text-ink-4 mt-1 leading-relaxed">
              WilsApp legt uw wensen vast als leidraad. Voor juridisch bindende vastlegging verwijzen wij naar uw notaris.
            </p>
          </>}
          defaultOpen={false}
        >
          <p className="text-sm text-ink-3">Wilt u bijzondere wensen vastleggen voor uw erfgenamen?</p>
          <Textarea value={data.erfgenaanWensen}
            onChange={e => upd('erfgenaanWensen', e.target.value)}
            placeholder={"Bijv:\n• Mijn kinderen gelijk behandelen\n• Mijn stiefkind Tom opnemen als erfgenaam\n• Mijn ex-partner uitslui ten van erfenis\n• € 5.000 apart zetten voor mijn kleinzoon"}
            maxLength={800} showCount className="min-h-[120px]" />
        </Section>

        {/* 4. Bezittingen */}
        <Section title={`${sNum()}. Aan wie geef ik wat?`} subtitle="Specifieke voorwerpen die u aan iemand wilt nalaten (legaten)."
          infoTitle="Bezittingen verdelen en legaten"
          infoContent={<>
            <p>Leg hier vast welke bezittingen u aan wie wilt nalaten. Denk aan sieraden, auto, meubels, kunst of herinneringsstukken.</p>
            <p className="mt-2"><strong>Wat is een legaat?</strong></p>
            <p>Een legaat is een specifiek geldbedrag of bezit dat u aan iemand wilt nalaten.</p>
            <ul className="list-disc list-inside text-xs text-ink-3 space-y-0.5 mt-1">
              <li>€ 5.000 aan een kleinkind</li>
              <li>Uw auto aan een neef</li>
              <li>Een schilderij aan een vriend</li>
              <li>Een bedrag aan een goed doel</li>
            </ul>
            <p className="mt-1.5 text-xs text-ink-3">Een legataris ontvangt alleen wat u specifiek aanwijst en wordt <em>geen</em> erfgenaam van de gehele nalatenschap.</p>
            <p className="mt-2">
              <a href="https://www.rijksoverheid.nl/onderwerpen/erven" target="_blank" rel="noopener noreferrer"
                 className="text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
                Rijksoverheid - Erfenis, erfgenamen en legaten →
              </a>
            </p>
            <p className="text-xs text-ink-4 mt-1 leading-relaxed">
              Dit is geen juridisch bindende verdeling. Voor bindende vastlegging heeft u een testament nodig.
            </p>
          </>}
          defaultOpen={false}
        >
          <p className="text-sm text-ink-3">Welke bezittingen wilt u aan wie nalaten?</p>

          {data.bezittingen.length === 0 && (
            <div className="rounded-xl bg-sand px-4 py-3 text-xs text-ink-4">
              <strong className="text-ink-3">Voorbeelden:</strong> Trouwring → Sophie · Horloge → Louise · Auto → zoon · Fotoalbums → beide kinderen
            </div>
          )}

          {data.bezittingen.map((item, i) => (
            <Card key={i} padding="sm">
              <div className="flex items-start gap-2">
                <div className="flex-1 grid gap-2 sm:grid-cols-3">
                  <Input label={i === 0 ? 'Bezitting' : ''} value={item.bezitting}
                    onChange={e => updBezitting(i, 'bezitting', e.target.value)}
                    placeholder="Bijv: Trouwring" />
                  <Input label={i === 0 ? 'Aan wie' : ''} value={item.persoon}
                    onChange={e => updBezitting(i, 'persoon', e.target.value)}
                    placeholder="Naam of relatie" />
                  <Input label={i === 0 ? 'Opmerking' : ''} value={item.opmerking}
                    onChange={e => updBezitting(i, 'opmerking', e.target.value)}
                    placeholder="Optioneel" />
                </div>
                <button onClick={() => delBezitting(i)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors flex-shrink-0 mt-5">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </Card>
          ))}
          <Button variant="outline" onClick={addBezitting} icon={<Plus className="h-4 w-4" />}>
            Bezitting toevoegen
          </Button>
        </Section>

        {/* 5. Digitale accounts */}
        <Section title={`${sNum()}. Digitale accounts`} subtitle="Wat moet er met uw online accounts gebeuren?"
          infoTitle="Digitale nalatenschap"
          infoContent={<>
            <p>Wat wilt u dat er na uw overlijden gebeurt met uw online accounts?</p>
            <p><strong>Verwijderen:</strong> account definitief opheffen via de helpdesk of overlijdensprocedure van het platform.</p>
            <p><strong>Herdenkingsprofiel:</strong> Facebook en Instagram bieden dit aan. Het profiel blijft zichtbaar maar kan niet meer worden ingelogd.</p>
            <p><strong>Overdragen:</strong> bijv. Google Takeout voor het archiveren van uw gegevens.</p>
          </>}
          defaultOpen={false}
        >
          <p className="text-sm text-ink-3">Wat moet er met uw online accounts gebeuren?</p>

          {data.digitaleAccounts.length === 0 && (
            <div className="rounded-xl bg-sand px-4 py-3 text-xs text-ink-4">
              <strong className="text-ink-3">Voorbeelden:</strong> Facebook → herdenkingsprofiel · LinkedIn → laten bestaan · Google → archiveren · Netflix → verwijderen
            </div>
          )}

          {data.digitaleAccounts.map((item, i) => (
            <Card key={i} padding="sm">
              <div className="flex items-start gap-2">
                <div className="flex-1 grid gap-2 sm:grid-cols-2">
                  <Select label={i === 0 ? 'Platform' : ''} options={BEKENDE_PLATFORMS}
                    value={item.platform} onChange={e => updDigitaal(i, 'platform', e.target.value)}
                    placeholder="Kies platform..." />
                  <Select label={i === 0 ? 'Actie' : ''} options={DIGITALE_ACTIES}
                    value={item.actie} onChange={e => updDigitaal(i, 'actie', e.target.value)}
                    placeholder="Kies actie..." />
                </div>
                <button onClick={() => delDigitaal(i)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors flex-shrink-0 mt-5">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </Card>
          ))}
          <Button variant="outline" onClick={addDigitaal} icon={<Plus className="h-4 w-4" />}>
            Account toevoegen
          </Button>
        </Section>

        {/* 6. Goede doelen */}
        <Section title={`${sNum()}. Goede doelen`} subtitle="Wilt u een goed doel begunstigen?" defaultOpen={false}
          infoTitle="Goede doelen"
          infoContent={<>
            <p>U kunt een deel van uw nalatenschap nalaten aan een goed doel. Dit kan een vast bedrag zijn of een percentage van de erfenis.</p>
            <p className="mt-2">Dit is ook een legaat - u wijst een specifiek bedrag of bezit aan voor een organisatie.</p>
            <p>Dit moet worden vastgelegd in een testament om juridisch geldig te zijn. Noteer hier uw wens als leidraad voor de notaris.</p>
            <p className="mt-2">
              <a href="https://www.rijksoverheid.nl/onderwerpen/erven" target="_blank" rel="noopener noreferrer"
                 className="text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
                Rijksoverheid - Erfenis nalaten aan goed doel →
              </a>
            </p>
          </>}
        >
          <p className="text-sm text-ink-3 mb-3">Wilt u een goed doel opnemen in uw nalatenschap?</p>

          {/* Wensregistratie disclaimer */}
          <div className="flex items-start gap-2.5 rounded-xl bg-status-infoBg border border-sage-200 px-4 py-3 mb-1">
            <span className="text-sm flex-shrink-0 mt-0.5">ℹ️</span>
            <p className="text-xs text-status-infoText leading-relaxed">
              Wilt u een goed doel daadwerkelijk laten erven? Leg dit vast in uw testament via uw notaris.
              Deze registratie in WilsApp is een <strong>wensregistratie</strong> als leidraad voor uw naasten.
            </p>
          </div>

          {data.goedeDoelenLijst.map((item, i) => (
            <Card key={i} padding="sm">
              <div className="flex items-start gap-2">
                <div className="flex-1 flex flex-col gap-2">
                  {/* Keuzelijst bekende goede doelen */}
                  <div>
                    {i === 0 && <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-1.5">Organisatie</p>}
                    <select
                      value={GOEDE_DOELEN_OPTIES.includes(item.organisatie) || item.organisatie === '' ? item.organisatie : 'Anders'}
                      onChange={e => {
                        const val = e.target.value
                        if (val === 'Anders') {
                          updGoedDoel(i, 'organisatie', '')
                        } else {
                          updGoedDoel(i, 'organisatie', val)
                        }
                      }}
                      className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage-400"
                    >
                      <option value="">Kies een goed doel...</option>
                      {GOEDE_DOELEN_OPTIES.map(g => <option key={g} value={g}>{g}</option>)}
                      <option value="Anders">Anders (zelf invullen)</option>
                    </select>
                    {/* Vrij tekstveld bij Anders of onbekende waarde */}
                    {(!GOEDE_DOELEN_OPTIES.includes(item.organisatie) && item.organisatie !== '') && (
                      <Input
                        value={item.organisatie}
                        onChange={e => updGoedDoel(i, 'organisatie', e.target.value)}
                        placeholder="Naam organisatie"
                        className="mt-2"
                      />
                    )}
                    {(item.organisatie === '' && !GOEDE_DOELEN_OPTIES.includes(item.organisatie)) && (
                      <Input
                        value={''}
                        onChange={e => updGoedDoel(i, 'organisatie', e.target.value)}
                        placeholder="Naam organisatie"
                        className="mt-2"
                      />
                    )}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input label={i === 0 ? 'Bedrag of %' : ''} value={item.bedragOfPct}
                      onChange={e => updGoedDoel(i, 'bedragOfPct', e.target.value)}
                      placeholder="€ 500 of 5%" />
                    <Input label={i === 0 ? 'Opmerking' : ''} value={item.opmerking}
                      onChange={e => updGoedDoel(i, 'opmerking', e.target.value)}
                      placeholder="Optioneel" />
                  </div>
                </div>
                <button onClick={() => delGoedDoel(i)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors flex-shrink-0 mt-5">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </Card>
          ))}
          <Button variant="outline" onClick={addGoedDoel} icon={<Plus className="h-4 w-4" />}>
            Goed doel toevoegen
          </Button>
        </Section>

        {/* V41: Huwelijkse voorwaarden */}
        {true  /* V41: altijd tonen - relevant voor alle samenlevingsvormen */ && (
          <Section title={`${sNum()}. Huwelijkse voorwaarden`} subtitle="Heeft u huwelijkse voorwaarden of een samenlevingscontract?" defaultOpen={false}
            infoTitle="Huwelijkse voorwaarden"
            infoContent={<>
              <p>Huwelijkse voorwaarden bepalen hoe vermogen is verdeeld tijdens het huwelijk en na overlijden.</p>
              <p>De notaris heeft deze gegevens nodig voor de afwikkeling van de nalatenschap.</p>
            </>}
          >
            <p className="text-sm text-ink-3 mb-3">Zijn er huwelijkse of partnerschapsvoorwaarden opgesteld?</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {([
                { v: 'yes',     l: 'Ja' },
                { v: 'no',      l: 'Nee' },
                { v: 'unknown', l: 'Onbekend' },
              ] as const).map(({ v, l }) => (
                <button key={v} type="button"
                  onClick={() => upd('huwelijkseVoorwaarden', v)}
                  className={`rounded-xl border-2 px-4 py-2 text-sm font-medium transition-all ${
                    data.huwelijkseVoorwaarden === v
                      ? 'border-sage-600 bg-sage-50 text-sage-800'
                      : 'border-sand-300 text-ink-3 hover:border-sage-300'
                  }`}>{l}</button>
              ))}
            </div>
            {data.huwelijkseVoorwaarden === 'yes' && (
              <div className="flex flex-col gap-3 animate-slide-down">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Notaris of kantoor" value={data.huwelijkseVoorwaardenNotaris}
                    onChange={e => upd('huwelijkseVoorwaardenNotaris', e.target.value)}
                    placeholder="Bijv: Notariskantoor De Vries" />
                  <Input label="Datum of jaar" value={data.huwelijkseVoorwaardenDatum}
                    onChange={e => upd('huwelijkseVoorwaardenDatum', e.target.value)}
                    placeholder="Bijv: 2018" />
                </div>
                <Input label="Locatie document" value={data.huwelijkseVoorwaardenLocatie}
                  onChange={e => upd('huwelijkseVoorwaardenLocatie', e.target.value)}
                  placeholder="Bijv: kluis thuis, notariskantoor" />
              </div>
            )}
          </Section>
        )}

        {/* 7. Onderneming - alleen tonen als van toepassing */}
        {answers.hasBusiness && (
          <Section title={`${sNum()}. Onderneming`} subtitle="Wat moet er met uw onderneming gebeuren?" defaultOpen={false}
            infoTitle="Onderneming in nalatenschap"
            infoContent={<>
              <p>Als u een onderneming heeft, is het belangrijk dat uw nabestaanden weten wat er mee moet gebeuren.</p>
              <p><strong>Voortzetten:</strong> is er een opvolger klaar?</p>
              <p><strong>Verkopen:</strong> wie regelt de verkoop?</p>
              <p><strong>Afwikkelen:</strong> wie rondt lopende contracten en verplichtingen af?</p>
            </>}
          >
            <p className="text-sm text-ink-3">Wat moet er met uw onderneming gebeuren na uw overlijden?</p>
            {/* V41: KvK-nummer */}
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="KvK-nummer" value={data.kvkNummer}
                onChange={e => upd('kvkNummer', e.target.value)} placeholder="Bijv: 12345678" />
              <Input label="Vestigingsadres" value={data.kvkVestigingsadres}
                onChange={e => upd('kvkVestigingsadres', e.target.value)} placeholder="Straat, woonplaats" />
            </div>
            <Input label="Beoogd opvolger / contactpersoon" value={data.kvkOpvolger}
              onChange={e => upd('kvkOpvolger', e.target.value)} placeholder="Naam + relatie of functie" />
            <div className="grid gap-3">
              {[
                { key: 'ondernemingWens'       as keyof NotarialData, label: 'Wat moet er met de onderneming gebeuren?', placeholder: 'Bijv: Bedrijf verkopen, voortzetten, afwikkelen...' },
                { key: 'ondernemingAfwikkel'   as keyof NotarialData, label: 'Wie moet de onderneming afwikkelen?',       placeholder: 'Naam en functie' },
                { key: 'ondernemingBoekhouder' as keyof NotarialData, label: 'Contactpersoon boekhouder / accountant',     placeholder: 'Naam, telefoonnummer, e-mail' },
                { key: 'ondernemingOverig'     as keyof NotarialData, label: 'Overige wensen',                             placeholder: 'Bijv: bedrijfsvoorraad verkopen, klanten informeren...' },
              ].map(({ key, label, placeholder }) => (
                <Textarea key={key} label={label}
                  value={data[key] as string}
                  onChange={e => upd(key, e.target.value as NotarialData[typeof key])}
                  placeholder={placeholder} maxLength={400} />
              ))}
            </div>
          </Section>
        )}

        {/* 8. Buitenlandse bezittingen - alleen tonen als van toepassing */}
        {answers.hasSecondHome && (
          <Section title={`${sNum()}. Buitenlandse bezittingen`} subtitle="Bezit u iets in het buitenland?" defaultOpen={false}
            infoTitle="Buitenlandse bezittingen"
            infoContent={<>
              <p>Bezittingen in het buitenland kunnen speciale procedures vereisen - elk land heeft eigen erfrecht.</p>
              <p>Leg hier vast wat u heeft en in welk land, zodat uw naasten weten waar ze moeten beginnen.</p>
              <p>Een notaris gespecialiseerd in internationaal erfrecht kan hierbij helpen.</p>
            </>}
          >
            <p className="text-sm text-ink-3">Welke buitenlandse bezittingen heeft u?</p>
            {data.buitenlandseBezittingen.map((item, i) => (
              <Card key={i} padding="sm">
                <div className="flex items-start gap-2">
                  <div className="flex-1 grid gap-2 sm:grid-cols-3">
                    <Input label={i === 0 ? 'Land' : ''} value={item.land}
                      onChange={e => {
                        const arr = [...data.buitenlandseBezittingen]
                        arr[i] = { ...arr[i], land: e.target.value }
                        upd('buitenlandseBezittingen', arr)
                      }}
                      placeholder="Bijv: Spanje" />
                    <Input label={i === 0 ? 'Omschrijving' : ''} value={item.omschrijving}
                      onChange={e => {
                        const arr = [...data.buitenlandseBezittingen]
                        arr[i] = { ...arr[i], omschrijving: e.target.value }
                        upd('buitenlandseBezittingen', arr)
                      }}
                      placeholder="Bijv: Vakantiewoning" />
                    <Input label={i === 0 ? 'Bijzonderheden' : ''} value={item.bijzonderheden}
                      onChange={e => {
                        const arr = [...data.buitenlandseBezittingen]
                        arr[i] = { ...arr[i], bijzonderheden: e.target.value }
                        upd('buitenlandseBezittingen', arr)
                      }}
                      placeholder="Bijv: Spaans notariscontact" />
                  </div>
                  <button onClick={() => upd('buitenlandseBezittingen', data.buitenlandseBezittingen.filter((_, j) => j !== i))}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors flex-shrink-0 mt-5">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </Card>
            ))}
            <Button variant="outline" onClick={addBuitenland} icon={<Plus className="h-4 w-4" />}>
              Buitenlandse bezitting toevoegen
            </Button>
          </Section>
        )}

        {/* 9. Persoonlijke boodschap */}
        <Section title={`${sNum()}. Persoonlijke boodschap voor uw nabestaanden`} subtitle="Een persoonlijke boodschap voor wie u achterlaat.">
          <p className="text-sm text-ink-3">
            Hier kunt u een persoonlijke boodschap achterlaten voor uw dierbaren.
            Alles wat u wilt meegeven dat niet in een ander onderdeel past.
          </p>
          <Textarea
            value={data.persoonlijkeBoodschap}
            onChange={e => upd('persoonlijkeBoodschap', e.target.value)}
            placeholder={"Lieve naasten,\n\nIk wil jullie laten weten dat...\n\n[Schrijf hier uw persoonlijke boodschap]"}
            maxLength={2000} showCount className="min-h-[160px]"
          />
        </Section>

      
        {status !== 'NOT_STARTED' && (
          <ClosingGuidance title="Uw notariële wensen zijn opgeslagen">
            Controleer regelmatig of uw wensen nog aansluiten bij uw persoonlijke en financiële situatie.
          </ClosingGuidance>
        )}

      {/* Sticky save-status zone - zichtbaar wanneer gebruiker onderaan de pagina scrolt */}
      <div className="sticky bottom-0 left-0 right-0 flex items-center justify-end
                      bg-white/90 backdrop-blur-sm border-t border-sand-200
                      px-4 py-2.5 mt-6 -mx-1 rounded-b-2xl">
        <SaveStatusBadge status={saveStatus} />
      </div>

            <NextStepBlock
              title="Uw voorbereiding delen"
              intro={<>Heeft u uw wensen ingevuld? Dan kunt u deze gebruiken als voorbereiding op een gesprek met uw notaris. Uw notaris kan uw wensen vervolgens samen met u bespreken en - indien gewenst - juridisch vastleggen.</>}
              actions={[
                { icon: '📄', label: 'Download notarisdossier', primary: true, onClick: () => navigate(DOSSIER_NOTARIS) },
                { icon: '📧', label: 'Verstuur voorbereiding naar mijn notaris', onClick: () => {
                  const subject = encodeURIComponent('Voorbereiding notariële wensen via WilsApp')
                  const body = encodeURIComponent('Beste,\n\nGraag zou ik een afspraak maken om mijn notariële wensen te bespreken. Ik heb deze als voorbereiding vastgelegd in WilsApp en kan het overzicht meenemen of meesturen.\n\nDit is een voorbereiding op een gesprek - geen opdracht, overeenkomst of reservering.\n\nMet vriendelijke groet')
                  window.location.href = `mailto:?subject=${subject}&body=${body}`
                } },
                { icon: '➕', label: 'Notaris toevoegen of wijzigen', onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
              ]}
              disclaimer="Dit is geen opdracht aan de notaris, geen overeenkomst en geen reservering - uitsluitend een voorbereiding op een gesprek."
            />

            <div className="flex flex-col gap-2 mt-2">
              <PrimaryActionButton onClick={async () => { await saveNow(); navigate(target) }} icon={<CheckCircle2 className="h-5 w-5" />}>
          {label}
        </PrimaryActionButton>
              <SecondaryActionButton onClick={async () => { await saveNow(); navigate(-1) }}>Terug naar vorige scherm</SecondaryActionButton>
            </div>
    </ModuleShell>
    </div>
  )
}
